import React, { useCallback } from "react";
import { includes, map } from "lodash";
import { useCustomFields } from "@radarly/core/custom-field/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { PivotCard } from "@radarly/models/cards";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { mergeClauses } from "@radarly/models/clause/merge";
import { getTermFromAggs } from "@radarly/models/search";
import { getFocusClause } from "../clause";
import { useFragment } from "../fragment";
import { useGenerateCSV } from "../hooks/useGenerateCSV";
import { CardProps } from "../model";
import HorizontalStackedBarChart from "./horizontal-stacked-bar-chart";

const RadarlyPivotCards = ({
  card,
  clause,
  from,
  to,
  height,
  openPostsPanel,
  timezone,
  forceReload,
  readOnly,
  nowDate,
}: CardProps<PivotCard>) => {
  const project = useCurrentProject();
  const fragment = useFragment(card.fragments[0], clause, from, to, timezone);
  const dataviz = card.dataviz;
  const pivot = getTermFromAggs(fragment.request.aggs);
  const against = getTermFromAggs(fragment.request.aggs[pivot].aggs);
  const fields = useCustomFields();
  const title = card.title;

  const { onGenerateCSV } = useGenerateCSV(card.title, fragment);

  const onClickGauge = useCallback(
    (
      uid: string | number,
      range?: { from: Date; to: Date },
      filter?: string | number
    ) => {
      const { from, to } = getDateFromClause(fragment.request.query);
      const pivotClause = getFocusClause(
        pivot,
        uid,
        includes(map(fields, "label"), pivot)
      );
      const againstClause = getFocusClause(
        against,
        filter,
        includes(map(fields, "label"), against)
      );
      const focusClause =
        uid && filter // Only merge when both filters `pivot` and `against` are present
          ? mergeClauses(pivotClause, againstClause)
          : uid
          ? pivotClause
          : againstClause;

      openPostsPanel({
        clause: fragment.request.query,
        period: {
          from: range ? range.from : from,
          to: range ? range.to : to,
        },
        title,
        timezone,
        focusClause,
      });
    },
    [
      against,
      fields,
      fragment.request.query,
      openPostsPanel,
      pivot,
      timezone,
      title,
    ]
  );

  return (
    <HorizontalStackedBarChart
      fragment={fragment}
      project={project}
      dataviz={dataviz}
      title={title}
      height={height}
      onClickItem={openPostsPanel && !readOnly ? onClickGauge : null}
      timezone={timezone}
      forceReload={forceReload}
      nowDate={nowDate}
      onGenerateCSV={onGenerateCSV}
    />
  );
};

export default RadarlyPivotCards;
